import React, { useEffect, useState } from 'react';
import { StatusBar, Style } from '@capacitor/status-bar';
import { Capacitor } from '@capacitor/core';

interface SafeAreaInsets {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

interface SafeAreaProviderProps {
  children: React.ReactNode;
  style?: React.CSSProperties;
  className?: string;
}

export const SafeAreaProvider: React.FC<SafeAreaProviderProps> = ({ 
  children, 
  style = {}, 
  className = '' 
}) => {
  const [isNative, setIsNative] = useState(false);

  useEffect(() => {
    const initializeSafeArea = async () => {
      // Check if we're running on a native platform
      const isNativePlatform = Capacitor.isNativePlatform();
      setIsNative(isNativePlatform);

      if (isNativePlatform) {
        try {
          // Configure status bar for native platforms
          await StatusBar.setStyle({ style: Style.Dark });
          await StatusBar.setBackgroundColor({ color: '#ffffff' });
          
          // Add CSS to document head for native safe area support
          const styleElement = document.createElement('style');
          styleElement.textContent = `
            /* Reset body and html for native platforms */
            html, body {
              margin: 0;
              padding: 0;
              height: 100%;
              overflow: hidden;
              position: fixed;
              width: 100%;
            }
            
            #root {
              height: 100vh;
              width: 100vw;
              overflow: hidden;
              position: relative;
            }
            
            :root {
              --sat: env(safe-area-inset-top, 0px);
              --sar: env(safe-area-inset-right, 0px);
              --sab: env(safe-area-inset-bottom, 0px);
              --sal: env(safe-area-inset-left, 0px);
            }
            
            .safe-area-provider {
              height: 100vh;
              width: 100vw;
              overflow: hidden;
              position: relative;
              display: flex;
              flex-direction: column;
            }
            
            .safe-area-top {
              padding-top: var(--sat);
              flex-shrink: 0;
            }
            
            .safe-area-bottom {
              padding-bottom: var(--sab);
              flex-shrink: 0;
            }
            
            .safe-area-left {
              padding-left: var(--sal);
            }
            
            .safe-area-right {
              padding-right: var(--sar);
            }
            
            .safe-area-horizontal {
              padding-left: var(--sal);
              padding-right: var(--sar);
            }
            
            .safe-area-vertical {
              padding-top: var(--sat);
              padding-bottom: var(--sab);
            }
            
            .safe-area-all {
              padding-top: var(--sat);
              padding-right: var(--sar);
              padding-bottom: var(--sab);
              padding-left: var(--sal);
            }
            
            /* Content area that can scroll */
            .safe-area-content {
              flex: 1;
              overflow-y: auto;
              overflow-x: hidden;
            }
          `;
          document.head.appendChild(styleElement);

          return () => {
            if (styleElement.parentNode) {
              styleElement.parentNode.removeChild(styleElement);
            }
          };
        } catch (error) {
          console.warn('Status bar configuration failed:', error);
        }
      }
    };

    initializeSafeArea();
  }, []);

  return (
    <div 
      className={`safe-area-provider ${className}`}
      style={style}
    >
      {children}
    </div>
  );
};

// Safe area utility components
export const SafeAreaTop: React.FC<{ children?: React.ReactNode; style?: React.CSSProperties }> = ({ 
  children, 
  style = {} 
}) => (
  <div 
    className="safe-area-top"
    style={style}
  >
    {children}
  </div>
);

export const SafeAreaBottom: React.FC<{ children?: React.ReactNode; style?: React.CSSProperties }> = ({ 
  children, 
  style = {} 
}) => (
  <div 
    className="safe-area-bottom"
    style={style}
  >
    {children}
  </div>
);

export const SafeAreaLeft: React.FC<{ children?: React.ReactNode; style?: React.CSSProperties }> = ({ 
  children, 
  style = {} 
}) => (
  <div 
    className="safe-area-left"
    style={style}
  >
    {children}
  </div>
);

export const SafeAreaRight: React.FC<{ children?: React.ReactNode; style?: React.CSSProperties }> = ({ 
  children, 
  style = {} 
}) => (
  <div 
    className="safe-area-right"
    style={style}
  >
    {children}
  </div>
);

export const SafeAreaHorizontal: React.FC<{ children?: React.ReactNode; style?: React.CSSProperties }> = ({ 
  children, 
  style = {} 
}) => (
  <div 
    className="safe-area-horizontal"
    style={style}
  >
    {children}
  </div>
);

export const SafeAreaVertical: React.FC<{ children?: React.ReactNode; style?: React.CSSProperties }> = ({ 
  children, 
  style = {} 
}) => (
  <div 
    className="safe-area-vertical"
    style={style}
  >
    {children}
  </div>
);

export const SafeAreaAll: React.FC<{ children?: React.ReactNode; style?: React.CSSProperties }> = ({ 
  children, 
  style = {} 
}) => (
  <div 
    className="safe-area-all"
    style={style}
  >
    {children}
  </div>
);

// Content area component for scrollable content
export const SafeAreaContent: React.FC<{ children?: React.ReactNode; style?: React.CSSProperties }> = ({ 
  children, 
  style = {} 
}) => (
  <div 
    className="safe-area-content"
    style={style}
  >
    {children}
  </div>
); 